import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lifebuoy, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RescueRow } from "@/components/leadRescue/RescueRow";
import { RESCUE_CHANGED, rescueChanged } from "@/components/layout/useRescueCount";

const DAYS = [7, 14, 30, 90];

export default function AdminLeadRescue() {
  const { user } = useAuth();
  const ok = user?.org_type === "internal" && ["super_admin", "admin", "employee"].includes(user?.role);
  if (!ok) return <div className="p-8 text-sm" data-testid="lead-rescue-denied">Only the internal team can use Lead Rescue.</div>;
  return <LeadRescueBody />;
}

function LeadRescueBody() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/lead-rescue", { params: { days } }).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const refresh = () => api.get("/admin/lead-rescue/alert").then((r) => setData((d) => (d ? { ...d, alert: r.data.alert } : d))).catch(() => {});
    window.addEventListener(RESCUE_CHANGED, refresh);
    return () => { window.removeEventListener(RESCUE_CHANGED, refresh); };
  }, []);
  const drop = (id) => setData((d) => ({ ...d, leads: d.leads.filter((l) => l.id !== id) }));
  const update = (lead) => setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === lead.id ? lead : l)) }));
  return (
    <div className="p-6 lg:p-8" data-testid="lead-rescue-page">
      <div className="label-mono">Leads</div>
      <h1 className="font-chivo font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none mb-2 flex items-center gap-3">
        <Lifebuoy size={36} weight="bold" /> Lead Rescue
      </h1>
      <p className="text-sm text-zinc-700 max-w-3xl mb-5">
        Leads held back because an answer was missing, so they never went to a buyer. Fill in what you learn (for example after a call),
        then press <b>Check again &amp; send</b>. A lead only goes out if it now passes every check.
      </p>
      <DailyAlert alert={data?.alert} onChecked={(alert) => { setData((d) => ({ ...d, alert })); rescueChanged(); load(); }} />
      <div className="flex items-center gap-1.5 mb-4" data-testid="lead-rescue-days">
        <span className="label-mono mr-1">Show last</span>
        {DAYS.map((n) => (
          <button key={n} type="button" onClick={() => setDays(n)} data-testid={`lead-rescue-days-${n}`}
                  className={`border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 ${n === days ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            {n} days
          </button>
        ))}
      </div>
      {data === null && <div className="label-mono">Loading…</div>}
      {data === false && <div className="text-sm text-red-700" data-testid="lead-rescue-error">Couldn&apos;t load. Refresh to try again.</div>}
      {data && !data.leads.length && (
        <div className="text-sm text-zinc-600 border-2 border-dashed border-zinc-400 p-8 text-center" data-testid="lead-rescue-empty">
          No held-back leads in the last {days} days.
        </div>
      )}
      {data && data.leads.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="lead-rescue-list">
          <div className="label-mono" data-testid="lead-rescue-count">{data.leads.length} held back</div>
          {data.leads.map((l) => <RescueRow key={l.id} lead={l} fields={data.fields} onDone={drop} onUpdate={update} />)}
        </div>
      )}
    </div>
  );
}

function DailyAlert({ alert, onChecked }) {
  const [busy, setBusy] = useState(false);
  const check = () => {
    setBusy(true);
    api.post("/admin/lead-rescue/check-now")
      .then((r) => { onChecked(r.data.alert); toast.success("Checked the last 24 hours"); })
      .catch(() => toast.error("Couldn't run the check"))
      .finally(() => setBusy(false));
  };
  const at = alert ? new Date(alert.checked_at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" }) : null;
  const hot = alert?.waiting > 0;
  return (
    <div className={`mb-5 border-2 border-black p-3 flex flex-wrap items-center gap-3 ${hot ? "bg-amber-100" : "bg-zinc-50"}`} data-testid="lead-rescue-alert">
      <div className="text-sm">
        <div className="label-mono">Daily check · every morning at 9am Eastern</div>
        {alert ? (
          <div data-testid="lead-rescue-alert-text">
            {at}: found <b>{alert.found}</b> held-back lead{alert.found === 1 ? "" : "s"} from the 24 hours before.
            {hot ? <>{" "}<b>{alert.waiting}</b> still need{alert.waiting === 1 ? "s" : ""} you.</> : " All handled."}
          </div>
        ) : <div data-testid="lead-rescue-alert-text">No daily check has run yet. The first one runs tomorrow at 9am Eastern.</div>}
      </div>
      <button type="button" onClick={check} disabled={busy} data-testid="lead-rescue-check-now"
              className="ml-auto border-2 border-black bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white transition-colors duration-100 disabled:opacity-40">
        <MagnifyingGlass size={12} weight="bold" /> {busy ? "Checking…" : "Check now"}
      </button>
    </div>
  );
}
